import {
  useQueryClient,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";

import {
  API_ENROLL_WEBAUTHN_FACTOR_URL,
  API_ACTIVATE_WEBAUTHN_FACTOR_URL,
} from "constants/apiUrls";
import { QueryKey } from "types/queryKey/queryKey";

import useGetApiClient from "./useGetApiClient";

// Shape returned by POST /api/factors/enroll-webauthn and POST /authn/factors/enroll-webauthn
// (mirrors Okta's WebAuthn factor activation object)
export type WebAuthnEnrollmentOptions = {
  factorId: string;
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: string; alg: number }>;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  excludeCredentials?: Array<{ id: string; type: string }>;
};

export const base64urlToBuffer = (base64url: string): ArrayBuffer => {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "=",
  );
  const binary = atob(padded);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buffer;
};

export const bufferToBase64url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

export class UserCancelledError extends Error {
  constructor() {
    super("Passkey registration was cancelled");
    this.name = "UserCancelledError";
  }
}

function useEnrollWebAuthnFactor(): UseMutationResult<void, Error, void> {
  const apiClient = useGetApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Step 1: Ask BFF to initiate WebAuthn enrollment with Okta
      const enrollResponse = await apiClient(API_ENROLL_WEBAUTHN_FACTOR_URL, {
        method: "POST",
      });

      if (!enrollResponse.ok) {
        return Promise.reject(await enrollResponse.json());
      }

      const options: WebAuthnEnrollmentOptions = await enrollResponse.json();

      // Step 2: Trigger the native browser WebAuthn registration ceremony
      const rawCredential = await navigator.credentials.create({
        publicKey: {
          challenge: base64urlToBuffer(options.challenge),
          rp: options.rp,
          user: {
            id: base64urlToBuffer(options.user.id),
            name: options.user.name,
            displayName: options.user.displayName,
          },
          pubKeyCredParams:
            options.pubKeyCredParams as PublicKeyCredentialParameters[],
          authenticatorSelection: options.authenticatorSelection,
          excludeCredentials: options.excludeCredentials?.map((cred) => ({
            id: base64urlToBuffer(cred.id),
            type: cred.type as PublicKeyCredentialType,
          })),
        },
      });

      if (!rawCredential) {
        return Promise.reject(new Error("Passkey registration was cancelled"));
      }

      const credential = rawCredential as PublicKeyCredential;

      const attestationResponse =
        credential.response as AuthenticatorAttestationResponse;

      // Step 3: Send the credential back to BFF to activate the factor in Okta
      const activateResponse = await apiClient(
        API_ACTIVATE_WEBAUTHN_FACTOR_URL,
        {
          method: "POST",
          body: JSON.stringify({
            factorId: options.factorId,
            attestation: bufferToBase64url(
              attestationResponse.attestationObject,
            ),
            clientData: bufferToBase64url(attestationResponse.clientDataJSON),
          }),
        },
      );

      if (!activateResponse.ok) {
        return Promise.reject(await activateResponse.json());
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKey.AllFactors]);
    },
  });
}

export default useEnrollWebAuthnFactor;
